import logo64 from '@/assets/icons/ui/logo64.png';
import { FILE_ICONS, MENU_ICONS, MENU_ITEMS, UI_CONSTANTS } from '@/constants';
import { useIDEState } from '@/hooks/use-ide-state';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { getAnalyticsConsent, setAnalyticsConsent } from '@/utils/analytics';
import React, { useEffect, useMemo, useState } from 'react';
import { AboutModal } from './about-modal';
import { MenuDropdown, MenuItemType } from './menu-dropdown';

export const MenuBar: React.FC = () => {
    const {
        state,
        openFile,
        closeTab,
        closeAllTabs,
        toggleFileExplorer,
        toggleOutputPanel,
        setModalOpen,
        toggleSearch,
    } = useIDEState();

    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [analyticsConsent, setAnalyticsConsentState] = useState<boolean | null>(null);

    // Initialize keyboard shortcuts
    useKeyboardShortcuts();

    // Listen for keyboard shortcut to open About modal
    useEffect(() => {
        const handleOpenAbout = () => {
            setIsAboutModalOpen(true);
        };

        document.addEventListener('open-about-modal', handleOpenAbout);
        return () => {
            document.removeEventListener('open-about-modal', handleOpenAbout);
        };
    }, []);

    // Update global modal state when modals open/close
    useEffect(() => {
        setModalOpen(isAboutModalOpen);
    }, [isAboutModalOpen, setModalOpen]);

    // Control body overflow when modal is open
    useEffect(() => {
        if (isAboutModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = '';
        };
    }, [isAboutModalOpen]);

    // Load current analytics consent state
    useEffect(() => {
        const consent = getAnalyticsConsent();
        setAnalyticsConsentState(consent?.hasConsent ?? null);
    }, []);

    // Memoize menu items to prevent recreation
    const closeMenuItem: MenuItemType = useMemo(
        () =>
            ({
                label: 'Close',
                icon: MENU_ICONS.CLOSE,
                shortcut: MENU_ITEMS.FILE[0].shortcut,
                action: () => state.activeTabId && closeTab(state.activeTabId),
                disabled: !state.activeTabId,
            }) as MenuItemType,
        [state.activeTabId, closeTab]
    );

    const closeAllMenuItem: MenuItemType = useMemo(
        () => ({
            label: 'Close All',
            icon: MENU_ICONS.CLOSE_ALL,
            shortcut: MENU_ITEMS.FILE[1].shortcut,
            action: closeAllTabs,
            disabled: state.tabs.length === 0,
        }),
        [state.tabs, closeAllTabs]
    );

    const openRecentSubmenu: MenuItemType[] = useMemo(() => {
        return state.recentFiles
            .slice(0, UI_CONSTANTS.RECENT_FILES_MAX_COUNT)
            .map(fileId => {
                const project = state.projects.find(p => p.files.some(f => f.id === fileId));
                const file = project?.files.find(f => f.id === fileId);
                return { fileId, project, file };
            })
            .filter(item => item.project && item.file) // Filter out items where project or file is not found
            .map(
                item =>
                    ({
                        label: `${item.project!.name}/${item.file!.name}`,
                        icon: FILE_ICONS.DEFAULT,
                        action: () => openFile(item.file!.id, item.project!.id),
                        disabled: false,
                    }) as MenuItemType
            );
    }, [state.recentFiles, state.projects, openFile]);

    const openRecentMenuItem: MenuItemType = useMemo(
        () => ({
            label: 'Open Recent',
            icon: MENU_ICONS.OPEN_RECENT,
            disabled: state.recentFiles.length === 0,
            submenu: openRecentSubmenu,
        }),
        [state.recentFiles, openRecentSubmenu]
    );

    const fileMenuItems: MenuItemType[] = useMemo(
        () => [closeMenuItem, closeAllMenuItem, openRecentMenuItem],
        [closeMenuItem, closeAllMenuItem, openRecentMenuItem]
    );

    const viewMenuItems: MenuItemType[] = useMemo(
        () => [
            {
                label: 'Explorer',
                icon: MENU_ICONS.EXPLORER,
                shortcut: MENU_ITEMS.VIEW[0].shortcut,
                action: toggleFileExplorer,
            },
            {
                label: 'Output',
                icon: MENU_ICONS.OUTPUT,
                shortcut: MENU_ITEMS.VIEW[1].shortcut,
                action: toggleOutputPanel,
            },
            {
                label: 'Search',
                icon: '🔍',
                shortcut: MENU_ITEMS.VIEW[2].shortcut,
                action: toggleSearch,
            },
        ],
        [toggleFileExplorer, toggleOutputPanel, toggleSearch]
    );

    const helpMenuItems: MenuItemType[] = useMemo(
        () => [
            {
                label: 'About',
                icon: MENU_ICONS.ABOUT,
                shortcut: MENU_ITEMS.HELP[0].shortcut,
                action: () => setIsAboutModalOpen(true),
            } as MenuItemType,
            {
                label: 'Analytics',
                icon: '📊',
                submenu: [
                    {
                        label: 'Accept',
                        icon: '✓',
                        action: () => {
                            setAnalyticsConsent(true);
                            setAnalyticsConsentState(true);
                        },
                        disabled: analyticsConsent === true,
                    } as MenuItemType,
                    {
                        label: 'Decline',
                        icon: '❌',
                        action: () => {
                            setAnalyticsConsent(false);
                            setAnalyticsConsentState(false);
                        },
                        disabled: analyticsConsent === false,
                    } as MenuItemType,
                ],
            } as MenuItemType,
        ],
        [analyticsConsent]
    );

    return (
        <div
            id='ide-menu-bar'
            className='h-8 bg-vscode-bg-secondary border-b border-gray-600/30 shadow-sm flex items-center px-2 text-sm'
            onContextMenu={e => e.preventDefault()}
        >
            <div id='ide-menu-items' className='flex items-center space-x-3'>
                <a href='/' className='hover:opacity-80 transition-opacity cursor-pointer mr-2'>
                    <img src={logo64} alt='Logo' className='w-6 h-6' />
                </a>
                <MenuDropdown label='File' items={fileMenuItems} />
                <MenuDropdown label='View' items={viewMenuItems} />
                <MenuDropdown label='Help' items={helpMenuItems} />
            </div>

            <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
        </div>
    );
};
